import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Activity, Target, Zap, BarChart3 } from "lucide-react";

const WINDOW_OPTIONS = [
  { label: "1h", hours: 1 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

const TIER_COLORS = {
  priority: "bg-red-500",
  regional: "bg-amber-500",
  national: "bg-blue-500",
  unknown: "bg-slate-400",
};

const STATUS_COLORS = {
  available: "bg-green-500",
  returning_empty: "bg-cyan-500",
  online: "bg-blue-500",
  scheduled: "bg-purple-500",
  busy: "bg-amber-500",
  offline: "bg-slate-400",
};

function fmtSec(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

function fmtPct(p) {
  if (p == null) return "—";
  return `${Math.round(p * 100)}%`;
}

function HourBars({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-xs text-slate-400 text-center py-4">No booking activity in this window.</p>;
  }
  const max = Math.max(...data.map(d => d.bookings_dispatched), 1);
  return (
    <div className="flex items-end gap-1 h-24" data-testid="metrics-hourly-bars">
      {data.map((d, i) => {
        const h = Math.max(2, Math.round((d.bookings_dispatched / max) * 88));
        const hour = new Date(d.hour);
        const label = `${hour.getHours()}:00`;
        return (
          <div key={i} className="flex flex-col items-center flex-1 min-w-0" title={`${d.hour}: ${d.bookings_dispatched} bookings`}>
            <div
              className="w-full bg-orange-500 rounded-sm hover:bg-orange-600 transition-colors"
              style={{ height: `${h}px` }}
              data-testid={`hourly-bar-${i}`}
            />
            <span className="text-[9px] text-slate-400 mt-0.5 truncate w-full text-center">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function TierPill({ tier, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`tier-pill-${tier}`}>
      <span className={`w-2 h-2 rounded-full ${TIER_COLORS[tier] || "bg-slate-400"}`} />
      <span className="capitalize text-slate-600 flex-1">{tier}</span>
      <span className="font-mono font-semibold text-slate-900">{count}</span>
      <span className="text-slate-400 text-[10px] w-9 text-right">{pct}%</span>
    </div>
  );
}

export default function DispatchMetricsPanel({ auth, API }) {
  const [hours, setHours] = useState(24);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dispatch/metrics?since_hours=${hours}`, auth());
      setMetrics(res.data);
    } catch (e) {
      console.error("metrics fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [API, auth, hours]);

  useEffect(() => {
    fetchMetrics();
    // Refresh every 60s — metrics are cheap aggregations.
    const t = setInterval(fetchMetrics, 60000);
    return () => clearInterval(t);
  }, [fetchMetrics]);

  if (!metrics) {
    return (
      <Card data-testid="dispatch-metrics-loading">
        <CardContent className="py-6 text-center text-xs text-slate-400">
          Loading dispatch metrics…
        </CardContent>
      </Card>
    );
  }

  const tierEntries = Object.entries(metrics.jobs_accepted_by_tier || {});
  const driverEntries = Object.entries(metrics.active_drivers_by_status || {});
  const total = metrics.total_dispatch_started || 0;

  return (
    <Card data-testid="dispatch-metrics-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-orange-500" />
            Dispatch performance
            {loading && <span className="text-[10px] text-slate-400 font-normal">(refreshing…)</span>}
          </CardTitle>
          <div className="flex gap-1" data-testid="metrics-window-picker">
            {WINDOW_OPTIONS.map(opt => (
              <button
                key={opt.hours}
                onClick={() => setHours(opt.hours)}
                className={`px-2 py-1 text-[11px] rounded border transition-colors ${
                  hours === opt.hours
                    ? "bg-orange-500 border-orange-500 text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:border-orange-300"
                }`}
                data-testid={`metrics-window-${opt.label}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top-line KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="border rounded-md p-3 bg-slate-50" data-testid="metric-fill-rate">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
              <Target className="h-3 w-3" /> Fill rate
            </div>
            <div className="text-2xl font-bold text-green-700">{fmtPct(metrics.fill_rate)}</div>
            <div className="text-[10px] text-slate-400">{metrics.total_accepted} / {total} accepted</div>
          </div>
          <div className="border rounded-md p-3 bg-slate-50" data-testid="metric-avg-accept">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
              <Zap className="h-3 w-3" /> Avg time-to-accept
            </div>
            <div className="text-2xl font-bold text-slate-900">{fmtSec(metrics.avg_time_to_accept_seconds)}</div>
            <div className="text-[10px] text-slate-400">across all tiers</div>
          </div>
          <div className="border rounded-md p-3 bg-slate-50" data-testid="metric-escalation">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
              <TrendingUp className="h-3 w-3" /> Avg escalation depth
            </div>
            <div className="text-2xl font-bold text-amber-700">
              {metrics.avg_escalation_depth != null ? metrics.avg_escalation_depth.toFixed(1) : "—"}
            </div>
            <div className="text-[10px] text-slate-400">tiers traversed before accept</div>
          </div>
          <div className="border rounded-md p-3 bg-slate-50" data-testid="metric-national-rate">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
              <Activity className="h-3 w-3" /> National broadcasts
            </div>
            <div className="text-2xl font-bold text-blue-700">{fmtPct(metrics.national_broadcast_rate)}</div>
            <div className="text-[10px] text-slate-400">{metrics.national_broadcast_count} required full broadcast</div>
          </div>
        </div>

        {/* Tier acceptance breakdown + time-to-accept by tier */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Jobs accepted by tier</p>
            <div className="space-y-1.5" data-testid="metrics-tier-breakdown">
              {tierEntries.length === 0 ? (
                <p className="text-xs text-slate-400">No accepted jobs in this window.</p>
              ) : (
                tierEntries.map(([tier, count]) => (
                  <TierPill key={tier} tier={tier} count={count} total={metrics.total_accepted} />
                ))
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Avg accept time by tier</p>
            <div className="space-y-1.5" data-testid="metrics-tier-accept-times">
              {Object.entries(metrics.avg_time_to_accept_by_tier_seconds || {}).map(([tier, secs]) => (
                <div key={tier} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full ${TIER_COLORS[tier] || "bg-slate-400"}`} />
                  <span className="capitalize text-slate-600 flex-1">{tier}</span>
                  <span className="font-mono font-semibold text-slate-900" data-testid={`accept-time-${tier}`}>{fmtSec(secs)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hourly trend + driver-by-status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold text-slate-700 mb-2">
              Booking volume — last {hours < 24 ? `${hours}h` : `${hours / 24}d`}
            </p>
            <HourBars data={metrics.hourly_booking_volume} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Active drivers by status</p>
            <div className="flex flex-wrap gap-1.5" data-testid="metrics-drivers-by-status">
              {driverEntries.map(([s, n]) => (
                <Badge
                  key={s}
                  className="bg-slate-100 text-slate-700 border-slate-200 flex items-center gap-1.5"
                  data-testid={`metrics-driver-status-${s}`}
                >
                  <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[s] || "bg-slate-400"}`} />
                  {s.replace("_", " ")}: <strong>{n}</strong>
                </Badge>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Unaccepted in flight: <strong>{metrics.unaccepted_in_dispatch}</strong>
            </p>
          </div>
        </div>

        <p className="text-[10px] text-slate-400">
          Updated {new Date(metrics.generated_at).toLocaleTimeString()} · auto-refreshes every 60s
        </p>
      </CardContent>
    </Card>
  );
}
